import { ChangeDetectionStrategy, Component, computed, effect, forwardRef, inject, input, output, signal, untracked } from '@angular/core';
import { NgTemplateOutlet, DatePipe } from '@angular/common';

import { NzTypographyModule } from 'ng-zorro-antd/typography';
import { NzButtonModule } from 'ng-zorro-antd/button';

import { TaskFavoriteComponent } from '@shared/_tasks/task-favorite.component';
import { TaskIdBadgeComponent } from '@shared/_tasks/task-id-badge.component';
import { TaskTextClassesDirective } from '@shared/_tasks/task-text-classes.directive';
import { AvatarComponent } from '@shared/_views/avatar';
import { VzDtComponent } from '@shared/_views/dt.component';
import { TagComponent } from '@shared/_views/tag';
import { DuedateTextClassesDirective } from '@shared/_tasks/duedate-text-classes.directive';
import { TaskLinkBadgeComponent } from '@shared/_tasks/task-link-badge.component';

import { BaseMin } from '@base/base-min';
import {
    Task, LayoutElement, LayoutElementType, TaskFieldType, TaskPriorityName, Workflow, TaskTypeDef, RecursivePartial, SM,
    traverseTreeArray, TaskLayoutBlock, VzParamType, TaskCardAlwaysShowIds, TaskFieldSystemType, stopEvent
} from '@models';
import { CacheService } from '@services/cache.service';
import { DayhoursPipe } from '@pipes/dayhours.pipe';

type LayoutFields = {
    show: { [id in TaskFieldSystemType]?: boolean };
    custom: TaskLayoutBlock[];
    map: { [id in TaskFieldSystemType]?: TaskLayoutBlock };
}

export interface LayoutTplEvent {
    type: 'menuButton',
    data: any,
    event: MouseEvent
}

@Component({
    selector: 'vz-layout-tpl, [vz-layout-tpl]',
    templateUrl: './layout-tpl.component.html',
    styleUrls: ['./layout-tpl.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        NgTemplateOutlet, DatePipe,
        NzTypographyModule, NzButtonModule,
        TaskIdBadgeComponent, AvatarComponent, VzDtComponent, TagComponent, DuedateTextClassesDirective, TaskTextClassesDirective,
        TaskFavoriteComponent, forwardRef(() => TaskLinkBadgeComponent), DayhoursPipe
    ]
})
export class LayoutTplComponent extends BaseMin {

    readonly rootElement    = input.required<LayoutElement>();
    readonly tTask          = input<Task>(undefined, { alias: 'task' });
    readonly ttDef          = input<RecursivePartial<TaskTypeDef>>(undefined, { alias: 'taskTypeDef' });
    readonly options        = input<any>(undefined, { alias: 'tplOptions' });
    readonly objectsMap     = input<SM<any>>();

    readonly events         = output<LayoutTplEvent>();

    readonly workflow       = signal<Workflow | undefined>(undefined);

    readonly fields         = computed<LayoutFields>(() => {
        const ttd = this.ttDef();
        const opt = this.options();
        const res: LayoutFields = { show: {}, custom: [], map: {} };
        if (ttd && ttd.layout) {
            traverseTreeArray<TaskLayoutBlock>(ttd.layout, 'children', block => {
                if (block.showInCard && block.type == TaskLayoutBlock.Type.Custom) {
                    block._id = block.fieldDef?.id;
                    res.custom.push(block);
                }
                if (block.type == TaskLayoutBlock.Type.System && block.systemFieldId) {
                    res.map[block.systemFieldId] = block;
                    if (
                        TaskCardAlwaysShowIds[block.systemFieldId] === true
                        || (block.showInCard && (block.required || !opt?.hideNotRequired))
                    ) {
                        res.show[block.systemFieldId] = true;
                    }
                }
            });
        }
        return res;
    });

    LET = LayoutElementType;
    TFT = TaskFieldType;
    TFST = TaskFieldSystemType;
    TPN = TaskPriorityName;
    VZPT = VzParamType;

    private readonly _cache = inject(CacheService);

    constructor() {
        super();
        effect(() => {
            const task = this.tTask();
            this.__U(['wf']);
            const wf = untracked(this.workflow);
            if (task?.workflowId && (!wf?.id || wf?.id != task.workflowId)) {
                this.workflow.set(undefined);
                this._S.wf = this._cache.watch('workflow', task.workflowId).subscribe(st => this.workflow.set(st?.item as Workflow));
            }
        });
    }

    sendEvent(event: MouseEvent, type: LayoutTplEvent['type'], data: any): void {
        stopEvent(event);
        this.events.emit({ type, data, event });
    }

}
